import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  EstadoVazio,
  Modal,
  Selo,
  Tabela,
  ValorPercentual,
  type ColunaTabela,
} from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { useCarteira } from '../hooks/useOperacao';
import { ErroDaApi } from './ErroDaApi';

interface VinculoDeDesagio {
  readonly assessorId: string;
  readonly assessorNome: string;
  readonly fornecedorId: string;
  readonly fornecedorNome: string;
  readonly desagioPercentual: number;
  readonly ativo: boolean;
}

/**
 * RN-016 — Quanto este assessor compra de cada fornecedor.
 *
 * O deságio é o preço de compra, e o mesmo fornecedor cobra diferente de
 * assessores diferentes — é daí que sai o spread. Por isso o número mora no
 * par (assessor, fornecedor), e não no cadastro do fornecedor.
 *
 * ## O que a tela não faz
 *
 * Mudar o deságio **não** mexe em contrato fechado: aquele já materializou o
 * percentual dele (RN-023). O aviso está na tela porque a expectativa contrária
 * é razoável — quem edita um preço espera que ele valha — e descobrir isso pelo
 * contrato errado é caro.
 */
export function DesagioDoAssessor(props: { readonly assessorId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<VinculoDeDesagio | null>(null);
  const [adicionando, setAdicionando] = useState(false);

  const vinculos = useQuery({
    queryKey: ['desagios', props.assessorId],
    queryFn: () =>
      requisicaoAutenticada<VinculoDeDesagio[]>(
        `/icms/suppliers/desagio?assessorId=${props.assessorId}`,
      ),
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['desagios', props.assessorId] });
  };

  const colunas: readonly ColunaTabela<VinculoDeDesagio>[] = [
    {
      chave: 'fornecedorNome',
      titulo: 'Fornecedor',
      larguraMinima: '20rem',
      celula: (v) => <span className="text-sm text-conteudo-primario">{v.fornecedorNome}</span>,
    },
    {
      chave: 'desagio',
      titulo: 'Deságio de compra',
      numerica: true,
      larguraMinima: '10rem',
      celula: (v) => <ValorPercentual percentual={v.desagioPercentual} />,
    },
    {
      chave: 'ativo',
      titulo: 'Situação',
      larguraMinima: '8rem',
      celula: (v) => (
        <Selo tom={v.ativo ? 'positivo' : 'neutro'}>{v.ativo ? 'Ativo' : 'Inativo'}</Selo>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '7rem',
      celula: (v) => (
        <Botao variante="sutil" tamanho="pequeno" onClick={() => setEditando(v)}>
          Alterar
        </Botao>
      ),
    },
  ];

  const linhas = vinculos.data ?? [];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">Deságio de compra</h2>
          <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
            O percentual que este assessor paga a cada fornecedor. A diferença para o que ele
            revende ao cliente é o spread dele.
          </p>
        </div>
        <Botao onClick={() => setAdicionando(true)}>Vincular fornecedor</Botao>
      </div>

      <Aviso tom="informacao">
        Alterar aqui vale para os <strong>próximos</strong> contratos. Os já fechados guardam o
        deságio que tinham no fechamento e não são recalculados.
      </Aviso>

      {vinculos.isError && <ErroDaApi titulo="Não foi possível carregar" erro={vinculos.error} />}

      {!vinculos.isPending && linhas.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum fornecedor vinculado"
          descricao="Este assessor ainda não compra de nenhum fornecedor. Vincule um para definir o deságio."
          acao={<Botao onClick={() => setAdicionando(true)}>Vincular fornecedor</Botao>}
        />
      ) : (
        <Tabela
          legenda="Deságio de compra por fornecedor"
          colunas={colunas}
          dados={linhas}
          chaveDaLinha={(v) => v.fornecedorId}
          chaveDeCabecalhoDaLinha="fornecedorNome"
          carregando={vinculos.isPending}
        />
      )}

      <ModalDeDesagio
        assessorId={props.assessorId}
        vinculo={editando}
        novo={adicionando}
        jaVinculados={linhas.map((l) => l.fornecedorId)}
        aoFechar={() => {
          setEditando(null);
          setAdicionando(false);
        }}
        aoSalvar={invalidar}
      />
    </section>
  );
}

/* ─── Modal ───────────────────────────────────────────────────────────────── */

function ModalDeDesagio(props: {
  readonly assessorId: string;
  readonly vinculo: VinculoDeDesagio | null;
  readonly novo: boolean;
  readonly jaVinculados: readonly string[];
  readonly aoFechar: () => void;
  readonly aoSalvar: () => void;
}): JSX.Element {
  const aberto = props.vinculo !== null || props.novo;

  const fornecedores = useCarteira('fornecedores');
  const [fornecedorId, setFornecedorId] = useState('');
  const [percentual, setPercentual] = useState('');

  const salvar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<VinculoDeDesagio>('/icms/suppliers/desagio', {
        metodo: 'PUT',
        corpo: {
          assessorId: props.assessorId,
          fornecedorId: props.vinculo?.fornecedorId ?? fornecedorId,
          desagioPercentual: Number(percentual.replace(',', '.')),
        },
      }),
    onSuccess: () => {
      props.aoSalvar();
      fechar();
    },
  });

  const fechar = (): void => {
    salvar.reset();
    setFornecedorId('');
    setPercentual('');
    props.aoFechar();
  };

  // O campo nasce com o valor atual quando é edição; o `key` do Modal força a
  // remontagem entre um vínculo e outro, então isto não vaza de uma linha para
  // a seguinte.
  const valorInicial = props.vinculo === null ? '' : String(props.vinculo.desagioPercentual);
  const valor = percentual === '' ? valorInicial : percentual;

  const numero = Number(valor.replace(',', '.'));
  const valido = valor !== '' && Number.isFinite(numero) && numero >= 0 && numero <= 100;
  const precisaFornecedor = props.vinculo === null && fornecedorId === '';

  const disponiveis = (fornecedores.data ?? []).filter(
    (f) => !props.jaVinculados.includes(f.businessId),
  );

  return (
    <Modal
      key={props.vinculo?.fornecedorId ?? 'novo'}
      aberto={aberto}
      aoFechar={fechar}
      titulo={props.vinculo === null ? 'Vincular fornecedor' : 'Alterar deságio'}
      descricao={props.vinculo?.fornecedorNome}
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => salvar.mutate()}
            disabled={!valido || precisaFornecedor}
            carregando={salvar.isPending}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {props.vinculo === null && (
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-conteudo-primario">Fornecedor</span>
            <select
              value={fornecedorId}
              onChange={(e) => setFornecedorId(e.target.value)}
              className="h-11 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            >
              <option value="">Selecione…</option>
              {disponiveis.map((f) => (
                <option key={f.businessId} value={f.businessId}>
                  {f.razaoSocial}
                </option>
              ))}
            </select>
            {disponiveis.length === 0 && !fornecedores.isPending && (
              <span className="text-xs text-conteudo-sutil">
                Todos os fornecedores já estão vinculados a este assessor.
              </span>
            )}
          </label>
        )}

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Deságio (%)</span>
          <input
            type="text"
            inputMode="decimal"
            value={valor}
            onChange={(e) => setPercentual(e.target.value)}
            placeholder="40"
            className="valor-fiscal h-11 w-32 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
          />
          <span className="text-xs text-conteudo-sutil">
            Percentual de 0 a 100 — 40 significa que o assessor paga 60% do valor do crédito.
          </span>
        </label>

        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
      </div>
    </Modal>
  );
}
